import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import './Management.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:1337';

const emptyForm = {
  titel: '',
  type: 'Voltijds',
  locatie: 'Waregem',
  beschrijving: '',
  vereisten: '',
  aanbod: '',
};

const VacatureManagement = () => {
  const { token } = useAuth();
  const [vacatures, setVacatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { fetchVacatures(); }, [token]);

  const fetchVacatures = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/vacatures?sort=createdAt:desc&publicationState=preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Kan vacatures niet laden');
      const data = await res.json();
      setVacatures(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const handleEdit = (v) => {
    const a = v.attributes;
    setFormData({
      titel: a.titel || '',
      type: a.type || 'Voltijds',
      locatie: a.locatie || 'Waregem',
      beschrijving: a.beschrijving || '',
      vereisten: a.vereisten || '',
      aanbod: a.aanbod || '',
    });
    setEditingId(v.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId
        ? `${API_URL}/api/vacatures/${editingId}`
        : `${API_URL}/api/vacatures`;

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          data: {
            ...formData,
            publishedAt: new Date().toISOString(),
          },
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err?.error?.message || 'Opslaan mislukt');
      }
      await fetchVacatures();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Vacature verwijderen?')) return;
    try {
      const res = await fetch(`${API_URL}/api/vacatures/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Verwijderen mislukt');
      await fetchVacatures();
    } catch (err) {
      setError(err.message);
    }
  };

  const set = (field) => (e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Vacatures</h1>
        <button className="btn-primary" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
          {showForm ? 'Annuleren' : '+ Vacature Toevoegen'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Functietitel *</label>
              <input type="text" value={formData.titel} onChange={set('titel')} required placeholder="bijv. Carwrap Technicus" />
            </div>
            <div className="form-group">
              <label>Type *</label>
              <select value={formData.type} onChange={set('type')} required>
                <option>Voltijds</option>
                <option>Deeltijds</option>
                <option>Voltijds / Deeltijds</option>
                <option>Freelance</option>
                <option>Stage</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Locatie *</label>
            <input type="text" value={formData.locatie} onChange={set('locatie')} required />
          </div>

          <div className="form-group">
            <label>Beschrijving *</label>
            <textarea value={formData.beschrijving} onChange={set('beschrijving')} rows="4" required placeholder="Omschrijf de functie..." />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Wat we vragen</label>
              <p className="field-hint">Eén vereiste per regel</p>
              <textarea value={formData.vereisten} onChange={set('vereisten')} rows="5" placeholder={"Rijbewijs B\nErvaring met folie\nNauwkeurig ingesteld"} />
            </div>
            <div className="form-group">
              <label>Wat we bieden</label>
              <p className="field-hint">Eén voordeel per regel</p>
              <textarea value={formData.aanbod} onChange={set('aanbod')} rows="5" placeholder={"Competitief loon\nOpleidingsmogelijkheden\nAangenaam werkklimaat"} />
            </div>
          </div>

          <button type="submit" className="btn-submit" disabled={saving}>
            {saving ? 'Opslaan...' : editingId ? 'Vacature Bijwerken' : 'Vacature Aanmaken'}
          </button>
        </form>
      )}

      {loading ? (
        <p className="loading">Vacatures worden geladen...</p>
      ) : vacatures.length === 0 ? (
        <p className="empty">Geen vacatures gevonden. Voeg er een toe!</p>
      ) : (
        <div className="items-table">
          <table>
            <thead>
              <tr>
                <th>Titel</th>
                <th>Type</th>
                <th>Locatie</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {vacatures.map((v) => {
                const a = v.attributes;
                return (
                  <tr key={v.id}>
                    <td><strong>{a.titel}</strong></td>
                    <td>{a.type}</td>
                    <td>{a.locatie}</td>
                    <td className="actions">
                      <button className="btn-edit" onClick={() => handleEdit(v)}>Bewerken</button>
                      <button className="btn-delete" onClick={() => handleDelete(v.id)}>Verwijderen</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VacatureManagement;
